import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useCallback, useEffect, useState } from "react";
import AppRoutes from "./routes/Routes";
import Header from "./components/header/Header";
import Sidebar from "./components/common/Sidebar";
import { MOBILE_BREAKPOINT_QUERY } from "./constants/commonConstants";
import useMediaQuery from "./hooks/useMediaQuery";
import { isAuthenticated } from "./utils/helper";
import styles from "./App.module.css";

/**
 * Layout component that conditionally renders Header and Sidebar
 */
const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showLayout = isAuthenticated() && location.pathname !== "/login";
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  // Open by default on desktop, collapsed (off-canvas) by default on mobile.
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    const handleUnauthorized = () => navigate("/login", { replace: true });
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [navigate]);

  // Sync the sidebar to the viewport when crossing the breakpoint:
  // collapse the drawer on small screens, reveal it on large ones.
  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // On mobile the sidebar overlays content, so selecting a link should dismiss it.
  const handleNavigate = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  return (
    <>
      {showLayout ? (
        <div
          className={`${styles.layoutContainer} ${
            isSidebarOpen ? "sidebar-open" : "sidebar-collapsed"
          }`}
        >
          <Sidebar isOpen={isSidebarOpen} onNavigate={handleNavigate} />
          <div
            className={`sidebar-backdrop ${isSidebarOpen ? "is-visible" : ""}`}
            onClick={closeSidebar}
            aria-hidden="true"
          />
          <div className={styles.mainWrapper}>
            <Header toggleSidebar={toggleSidebar} />
            <main className="main-content">
              <AppRoutes />
            </main>
          </div>
        </div>
      ) : (
        <main className="main-content">
          <AppRoutes />
        </main>
      )}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Layout />
    </BrowserRouter>
  );
}

export default App;
